import { FC, useEffect, useMemo } from "react"
import Checkbox from "@/components/Checkbox"
import { useAppDispatch, useAppSelector } from "@/redux/hooks"
import {
  selectCampaignSetupDomains,
  setSelectedDomains,
  setSendMode,
} from "@/redux/campaignSetupSlice"
import { mailboxCountsAsGraphReady } from "@/utils/mailboxReadiness"
import {
  domainSendCapacity,
  selectedDomainsSendCapacity,
} from "@/utils/domainCapacity"
import { useAuth } from "@/hooks/useAuth"
import type { Domain } from "@/types/entities/domain"
import type { EditCampaignPayload } from "@/types/entities/campaign"

type Props = {
  domains: Domain[]
  onChange: (body: EditCampaignPayload) => void
  onDisabledChange: (disabled: boolean) => void
}

const domainReadyMailboxCount = (domain: Domain) =>
  domain.readyMailboxCount ??
  domain.mailboxes?.filter(mailboxCountsAsGraphReady).length ??
  0

const uniqueDomainsById = (domains: Domain[]) =>
  domains.filter(
    (domain, index, allDomains) =>
      allDomains.findIndex(({ _id }) => _id === domain._id) === index,
  )

const domainIsManaged = (domain: Domain) => {
  if (domain.dsEnabledAt && !domain.dsDisabledAt) return true
  if (domain.dsEnabled === true && !domain.dsDisabledAt) return true

  const status = String(domain.dreamSenderStatus || "")
    .trim()
    .toLowerCase()
  return !domain.dsDisabledAt && ["enabled", "active", "ready"].includes(status)
}

const domainStatusText = (domain: Domain) => {
  if (domain.inUseByCampaign) return "In use"
  const capacity = domainSendCapacity(domain)
  return capacity > 0 ? `Up to ${capacity.toLocaleString()}/day` : "Preparing"
}

const domainSelectable = (domain: Domain) =>
  !domain.inUseByCampaign && domainSendCapacity(domain) > 0

const domainDisabledText = (domain: Domain) =>
  domain.inUseByCampaign
    ? `In use by ${domain.lockedByCampaign?.name || "another campaign"}`
    : "Preparing"

const domainRowStatusText = (
  domain: Domain,
  selectable: boolean,
  isAdmin: boolean,
) => {
  if (!selectable) return domainDisabledText(domain)
  if (!isAdmin) return domainStatusText(domain)
  return `${domainReadyMailboxCount(domain)} ready, ~${domainSendCapacity(
    domain,
  )} sends/day`
}

const CampaignSenderView: FC<Props> = ({
  domains,
  onChange,
  onDisabledChange,
}: Props) => {
  const dispatch = useAppDispatch()
  const { isAdmin } = useAuth()
  const selectedDomains = useAppSelector(selectCampaignSetupDomains)
  const managedDomains = domains.filter(domainIsManaged)
  const liveDomainsById = useMemo(
    () => new Map(domains.map((domain) => [domain._id, domain])),
    [domains],
  )
  const domainOptions = uniqueDomainsById([
    ...selectedDomains,
    ...managedDomains,
  ]).map((domain) => liveDomainsById.get(domain._id) ?? domain)

  useEffect(() => {
    if (!liveDomainsById.size || !selectedDomains.length) return
    let changed = false
    const reconciled = selectedDomains.map((domain) => {
      const live = liveDomainsById.get(domain._id)
      if (live && domainSendCapacity(live) !== domainSendCapacity(domain)) {
        changed = true
        return live
      }
      return live ?? domain
    })
    if (changed) dispatch(setSelectedDomains(reconciled))
  }, [liveDomainsById, selectedDomains, dispatch])

  useEffect(() => {
    dispatch(setSendMode("ds_managed_rotation"))
  }, [dispatch])

  useEffect(() => {
    onChange({
      sendMode: "ds_managed_rotation",
      domainsId: selectedDomains.map(({ _id }) => _id),
    })
  }, [onChange, selectedDomains])
  useEffect(() => {
    onDisabledChange(selectedDomains.length === 0)
    return () => {
      onDisabledChange(false)
    }
  }, [onDisabledChange, selectedDomains.length])

  const handleDomainToggle = (checked: boolean, domainId: string) => {
    const domain = domainOptions.find(({ _id }) => _id === domainId)!
    if (checked) {
      if (!domainSelectable(domain)) return
      dispatch(setSelectedDomains(selectedDomains.concat(domain)))
      return
    }

    dispatch(
      setSelectedDomains(
        selectedDomains.filter(({ _id }) => _id !== domain._id),
      ),
    )
  }

  const selectedDomainCapacity = selectedDomainsSendCapacity(selectedDomains)
  const selectedDomainReadyMailboxes = selectedDomains.reduce(
    (total, domain) => total + domainReadyMailboxCount(domain),
    0,
  )

  return (
    <div className="flex flex-col gap-5">
      <h3 className="text-lg leading-7 font-semibold">Domains</h3>
      <div className="flex flex-col gap-4">
        <div className="rounded-md border border-outline overflow-hidden">
          {domainOptions.length ? (
            domainOptions.map((domain) => {
              const selected = selectedDomains.some(
                ({ _id }) => _id === domain._id,
              )
              const selectable = domainSelectable(domain) || selected
              return (
                <label
                  key={domain._id}
                  className={`flex items-center justify-between gap-4 border-b border-outline px-4 py-3 last:border-b-0 ${
                    selectable
                      ? "cursor-pointer"
                      : "cursor-not-allowed opacity-60"
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <Checkbox
                      onChange={({ target: { checked } }) =>
                        handleDomainToggle(checked, domain._id)
                      }
                      checked={selected}
                      disabled={!selectable}
                      className="checkbox-primary h-[18px] w-[18px]"
                    />
                    <span className="text-sm leading-6">{domain.name}</span>
                  </span>
                  <span className="text-xs opacity-60">
                    {domainRowStatusText(domain, selectable, Boolean(isAdmin))}
                  </span>
                </label>
              )
            })
          ) : (
            <div className="my-10 text-center text-base leading-6 opacity-60">
              No DreamSender domains available.
            </div>
          )}
        </div>
        {selectedDomains.length ? (
          <div className="rounded-md border border-outline bg-bg-default px-4 py-3 text-sm">
            {isAdmin ? (
              <>
                {selectedDomains.length} domains, {selectedDomainReadyMailboxes}{" "}
                ready mailboxes, ~{selectedDomainCapacity} sends/day
              </>
            ) : (
              <>
                {selectedDomains.length} domains selected, up to{" "}
                {selectedDomainCapacity.toLocaleString()} sends/day
              </>
            )}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default CampaignSenderView
